import type {
  AspectBreakdownDto,
  TopicCountDto,
} from '@up2stay/up2promo-api-client';
import { Card, cn, SegmentedControl } from '@up2stay/up2promo-ui';
import { useState } from 'react';

import { CHART } from '@/core/components/reputation/reputation-chart-config';
import { REPUTATION_SECTION_TITLE_CLASS } from '@/core/components/reputation/reputation-surface';
import type { Dictionary } from '@/core/providers/dictionary-provider';

const LIMIT = 8;

type Mode = 'topics' | 'aspects';
type Dict = Dictionary['reputation']['analytics'];

function Row({
  label,
  count,
  width,
  segments,
  onSelect,
}: {
  label: string;
  count: number;
  /** Bar length relative to the top row, 0..100. */
  width: number;
  segments: { color: string; share: number }[];
  onSelect?: () => void;
}) {
  const content = (
    <>
      <span className="truncate text-left max-md:col-start-1 max-md:row-start-1">
        {label}
      </span>
      <span className="flex h-2 overflow-hidden rounded-full bg-muted max-md:col-span-2 max-md:row-start-2 md:h-[22px] md:rounded-md">
        <span
          className="flex h-full"
          style={{ width: `${Math.max(4, width)}%` }}
        >
          {segments.map((segment, i) =>
            segment.share > 0 ? (
              <span
                key={i}
                className="h-full"
                style={{
                  width: `${segment.share * 100}%`,
                  background: segment.color,
                }}
              />
            ) : null,
          )}
        </span>
      </span>
      <span className="text-right font-mono text-xs tabular-nums max-md:col-start-2 max-md:row-start-1">
        {count}
      </span>
    </>
  );
  const rowClass =
    'grid min-h-8 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 text-[13px] md:grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)_2rem]';
  return onSelect ? (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        rowClass,
        'rounded-md hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      {content}
    </button>
  ) : (
    <div className={rowClass}>{content}</div>
  );
}

/** "What guests mention": free-form topics or taxonomy aspects, by frequency. */
export function AnalyticsMentionsCard({
  aspects,
  topics,
  labels,
  onSelectAspect,
  className,
}: {
  aspects: AspectBreakdownDto[];
  topics: TopicCountDto[];
  labels: Dict;
  onSelectAspect: (key: string) => void;
  className?: string;
}) {
  const [mode, setMode] = useState<Mode>(
    topics.length > 0 ? 'topics' : 'aspects',
  );
  const rows =
    mode === 'aspects'
      ? aspects.slice(0, LIMIT).map((aspect) => ({
          key: aspect.key,
          label: aspect.label,
          count: aspect.total,
          segments: [
            {
              color: CHART.positive,
              share: aspect.total ? aspect.positive / aspect.total : 0,
            },
            {
              color: CHART.neutral,
              share: aspect.total ? aspect.neutral / aspect.total : 0,
            },
            {
              color: CHART.negative,
              share: aspect.total ? aspect.negative / aspect.total : 0,
            },
          ],
          onSelect: () => onSelectAspect(aspect.key),
        }))
      : topics.slice(0, LIMIT).map((topic) => ({
          key: topic.topic,
          label: topic.topic,
          count: topic.count,
          segments: [{ color: CHART.neutral, share: 1 }],
          onSelect: undefined,
        }));
  const max = Math.max(...rows.map((row) => row.count), 1);

  return (
    <Card className={cn('flex min-w-0 flex-col gap-4 p-5 sm:p-6', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className={REPUTATION_SECTION_TITLE_CLASS}>
            {labels.mentions.title}
          </h2>
          <p className="text-muted-foreground mt-0.5 text-[12.5px]">
            {mode === 'aspects'
              ? labels.mentions.subtitleAspects
              : labels.mentions.subtitleTopics}
          </p>
        </div>
        <SegmentedControl
          className="max-md:hidden"
          ariaLabel={labels.mentions.title}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'topics', label: labels.mentions.topics },
            { value: 'aspects', label: labels.mentions.aspects },
          ]}
        />
      </div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {mode === 'aspects' ? labels.aspects.empty : labels.topics.empty}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <Row
              key={row.key}
              label={row.label}
              count={row.count}
              width={(row.count / max) * 100}
              segments={row.segments}
              onSelect={row.onSelect}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
